import { useEffect, useRef, type Dispatch, type RefObject } from "react";

interface UseAutoScrollNewMessagesArgs {
  channelId: string;
  latestMessageId: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messageById: Map<string, any>;
  userId: string | undefined;
  scrollRef: RefObject<HTMLDivElement | null>;
  isNearBottomRef: RefObject<boolean>;
  setShowJumpToBottom: Dispatch<React.SetStateAction<boolean>>;
  setNewMessagesPill: Dispatch<React.SetStateAction<number>>;
  /**
   * Tracking values whose identity must keep the effect's dependency
   * list byte-identical with the original inline implementation. The
   * effect doesn't read them — they're here to preserve fire cadence.
   */
  scrollAnchor: unknown;
  dispatchWindow: unknown;
}

/**
 * Reacts to the latest-message-id changing within a channel:
 *
 *   • If the user is at the bottom, hard-scroll to bottom (the window
 *     reconciliation already handles pin-bottom — dispatching here
 *     would cause a redundant render).
 *   • Otherwise, if the new message is from a remote user, increment
 *     the new-message pill and surface the jump-to-bottom button.
 *
 * Resets its tracking on channel switch so the first message in a
 * newly-opened channel doesn't count as "new".
 *
 * Intentional: dependencies on `scrollAnchor`/`dispatchWindow` are
 * left out (they're ref-based / stable callbacks); preserve the exact
 * same dep list as the original inline effect.
 */
export function useAutoScrollNewMessages({
  channelId,
  latestMessageId,
  messageById,
  userId,
  scrollRef,
  isNearBottomRef,
  setShowJumpToBottom,
  setNewMessagesPill,
  scrollAnchor,
  dispatchWindow,
}: UseAutoScrollNewMessagesArgs) {
  const prevLatestIdRef = useRef(latestMessageId);
  const prevChannelIdRef = useRef(channelId);

  useEffect(() => {
    // Reset tracking on channel switch
    if (prevChannelIdRef.current !== channelId) {
      prevChannelIdRef.current = channelId;
      prevLatestIdRef.current = latestMessageId;
      return;
    }
    const prevId = prevLatestIdRef.current;
    prevLatestIdRef.current = latestMessageId;
    if (latestMessageId === prevId) return; // No new message

    if (isNearBottomRef.current) {
      // At bottom — just do a direct DOM scroll. The window reconciliation
      // already handles pin-to-bottom when isNearBottom is true, so
      // dispatching pin-bottom here would cause a redundant render cycle.
      const el = scrollRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
      setShowJumpToBottom(false);
      setNewMessagesPill(0);
    } else {
      // Scrolled up — show new messages pill (don't auto-scroll)
      // Check if it's a remote message (not from current user)
      const newMsg = latestMessageId ? messageById.get(latestMessageId) : null;
      if (newMsg && newMsg.user_id !== userId) {
        setNewMessagesPill((prev) => prev + 1);
        setShowJumpToBottom(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserves the original effect's exact dep list (ref-based scrollRef/isNearBottomRef + stable setters intentionally omitted)
  }, [
    latestMessageId,
    channelId,
    scrollAnchor,
    dispatchWindow,
    messageById,
    userId,
  ]);
}
